import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { selectCustomer } from '@oneqrew-partnertool/core';
import { Customer, SalesPartner } from '@oneqrew-partnertool/models';
import { PrimeNgModule } from '@oneqrew-partnertool/primeng';
import { TagComponent } from '@oneqrew-partnertool/tag';
import { SharedModule } from '@oneqrew-partnertool/utils';
import { take } from 'rxjs/operators';
import * as textData from '../../../../../shared-assets/data/pages/order-page.json';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'lib-new-customer-contact',
  standalone: true,
  imports: [SharedModule, PrimeNgModule],
  templateUrl: './new-customer-contact.component.html',
  styleUrls: ['./new-customer-contact.component.scss']
})
export class NewCustomerContactComponent implements OnInit {
  @Input() isSendingOffer: boolean = false;
  @Input() sendOfferResult?: string = undefined;
  @Input() isNewCustomer = false;
  @Input() isCheckoutPage = false;
  @Output() customerContactForm$ = new EventEmitter<FormGroup>();
  @Output() onSubmit = new EventEmitter<void>();
  class = 'new-customer-contact';
  customerContactForm!: FormGroup;
  fieldLabelData: { [key: string]: string } = {};
  fields: Array<{ name: string; label: string; colSize: string; isRequired: boolean }> = [];
  textData = textData;
  salutation = '';
  salutationOpener = '';
  salutationSelect: { label: string; value: string }[] = [];
  salesPartnerSelect: { label: string | undefined; value: number | undefined }[] = [];
  selectedSalesPartner: number | undefined;
  salesPartners: SalesPartner[] = [];

  constructor(private fb: FormBuilder, private store: Store, private route: ActivatedRoute) {
    this.customerContactForm = this.fb.group({
      salutation: ['']
    });
  }

  ngOnInit(): void {
    this.salutationSelect = [
      { label: 'Herr', value: 'Herr' },
      { label: 'Frau', value: 'Frau' },
      { label: 'Ohne', value: 'Ohne ' }
    ];

    this.store
      .select(selectCustomer)
      .pipe(take(1))
      .subscribe((storedCustomer: Customer) => {
        this.initializeForm(storedCustomer);
      });

    this.route.data.subscribe((data: { [key: string]: any }) => {
      this.salesPartners = data['salesPartners'];
      this.salesPartnerSelect = this.getSalesPartnerSelect(this.salesPartners);
    });

    this.customerContactForm$.emit(this.customerContactForm);
  }

  sendOffer(): void {
    this.onSubmit.emit();
  }

  private initializeForm(storedCustomer: Customer): void {
    this.customerContactForm = this.fb.group({
      salutation: [storedCustomer.salutation, [Validators.required, Validators.minLength(2)]],
      firstName: [storedCustomer.firstName, [Validators.required, Validators.minLength(2)]],
      lastName: [storedCustomer.lastName, [Validators.required, Validators.minLength(2)]],
      company: [storedCustomer.company, [Validators.required, Validators.minLength(2)]],
      email: [storedCustomer.email, [Validators.required, Validators.email]],
      salesPartnerEmail: [null, [Validators.required, Validators.email]],
      salesPartner: [storedCustomer.salesPartner?.id, [Validators.required]]
    });
  }

  // Custom validator to check if confirmEmail matches email
  emailMatchValidator(control: AbstractControl) {
    const email = this.customerContactForm?.get('email')?.value;
    const confirmEmail = control.value;
    return email && confirmEmail && email !== confirmEmail ? { emailMismatch: true } : null;
  }

  getErrorMessage(field: string, label: string): string {
    const control = this.customerContactForm.get(field);
    if (control?.hasError('required')) {
      return `Bitte ${label} eingeben.`;
    }

    if (control?.hasError('pattern')) {
      return `Ungültige ${label}.`;
    }

    if (control?.hasError('email') || control?.hasError('salesPartnerEmail')) {
      return `Korrekte E-Mail-Adresse eingeben.`;
    }

    if (control?.hasError('minlength')) {
      return `${label} must be at least 2 characters long.`;
    }

    return '';
  }

  private getSalesPartnerSelect(salesPartners: SalesPartner[]): { label: string; value: number }[] {
    const sortedSalePartners = salesPartners
      .filter((s) => s.name && s.name != 'NOT FOUND IN BS TABLE')
      .sort((a: SalesPartner, b: SalesPartner) => {
        const isANumber = /^\d/.test(a.name!!);
        const isBNumber = /^\d/.test(b.name!!);

        // Numbers first
        if (isANumber && !isBNumber) return -1;
        if (!isANumber && isBNumber) return 1;

        // If both are numbers or both are not, sort alphabetically
        return a.name!!.localeCompare(b.name!!, undefined, { numeric: true });
      });
    return sortedSalePartners.map((s) => ({ label: s.name!!, value: s.id!! }));
  }
}
